import React, { useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import LendersSearchBlock from './components/LendersSearchBlock/LendersSearchBlock'
import ConfirmDialog from '../../components/ConfirmDialog'
import LendersTableBlock from './components/LendersTableBlock/LendersTableBlock'
import { useDeleteLenderMutation, useSearchLenderMutation } from '../../services'
import { DeleteLenderApiArg } from '../../models'
import { useDispatch, useSelector } from 'react-redux'
import { enqueueNotification } from '../../redux/slices/notificationsSlice'
import { appRoute } from '../../constants'
import { getUserAccessRights } from '../../redux/slices/authSlice'
import { useNavigate } from 'react-router-dom'
import { Button, Card, Container, Grid } from '@mui/material'
import CustomBreadcrumbs from '../../components/custom-breadcrumbs/custom-breadcrumbs'
import Iconify from '../../components/iconify'

interface Props {}
type TFormFields = {
  searchString: string
  isIncludeInactive: boolean
}

const LendersList = ({}: Props) => {
  const dispatch = useDispatch()
  const userAccess = useSelector(getUserAccessRights)
  const navigate = useNavigate()
  const [formValues, setFormValues] = useState<TFormFields>()
  const [searchLender, { data: searchLenderData, isLoading: searchLenderIsLoading }] = useSearchLenderMutation()
  const [deleteConfirmDialog, setDeleteConfirmDialog] = React.useState<{ lenderId: string } | undefined>(undefined)
  const [deleteLender, { isSuccess: deleteLenderIsSuccess }] = useDeleteLenderMutation()

  const handleSubmit = (values: TFormFields) => {
    setFormValues(values)
    searchLender({
      data: {
        searchString: values?.searchString || null,
        isIncludeInactive: values?.isIncludeInactive,
      },
    })
  }

  const handleDelete = () => {
    deleteLender(deleteConfirmDialog as DeleteLenderApiArg)
    setDeleteConfirmDialog(undefined)
  }

  useEffect(() => {
    if (deleteLenderIsSuccess) {
      dispatch(enqueueNotification({ message: 'Delete Successful' }))
      handleSubmit(formValues as TFormFields)
    }
  }, [deleteLenderIsSuccess])

  useEffect(() => {
    if (!userAccess.CanAccessLenders) {
      dispatch(
        enqueueNotification({
          message:
            'Access Denied: Sorry, your current access rights do not permit you to enter this section. If you believe you should have access, please contact your system administrator or help desk for assistance.',
          options: { variant: 'error' },
        }),
      )
      navigate(appRoute.Home)
    }
  }, [userAccess, appRoute])

  return (
    <Container>
      <CustomBreadcrumbs
        heading='Lenders'
        links={[
          {
            name: 'List',
          },
        ]}
        action={
          <Button
            onClick={() => navigate(appRoute?.LenderAddEdit.replace(':lenderId', 'add'))}
            variant='contained'
            startIcon={<Iconify icon='mingcute:add-line' />}
          >
            Add new
          </Button>
        }
        sx={{
          mb: {
            xs: 3,
            md: 5,
          },
        }}
      />
      <Form
        onSubmit={handleSubmit}
        initialValues={{ searchString: null, isIncludeInactive: false }} // require to avoid first debounce search
      >
        {({ handleSubmit, form: { submit } }: any) => (
          <form onSubmit={handleSubmit}>
            <Card>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LendersSearchBlock submit={submit} />
                </Grid>
                <Grid item xs={12}>
                  <LendersTableBlock deleteLender={setDeleteConfirmDialog} loading={searchLenderIsLoading} searchLenderData={searchLenderData} />
                </Grid>
              </Grid>
            </Card>
            <ConfirmDialog
              open={!!deleteConfirmDialog}
              onOk={handleDelete}
              handleClose={() => setDeleteConfirmDialog(undefined)}
              notification={'Lender will be removed'}
            />
          </form>
        )}
      </Form>
    </Container>
  )
}

export default LendersList
